import React from "react";
import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";
import { RendererProps } from "../../../shared/renderers";
import { messages } from "../experiment.messages";

interface LiftCellRowData {
  id: string;
  hasLift?: boolean;
  confidence?: number; // 0..100 on the list item (NOT a fraction, unlike the results payload)
  liftTooltipKey?: string; // "leadingA" | "leadingB" | "noWinner" | "insufficientData"
  sampleProgress?: number; // fraction, unclamped
}

type Tone = "a" | "b" | "neutral";

const Value = styled.div<{ $tone: Tone }>`
  font-weight: 600;
  color: ${({ theme, $tone }) =>
    $tone === "a"
      ? theme.orangeBackground
      : $tone === "b"
      ? theme.darkBlueBackground
      : theme.regularTextColor};
`;

const Subtitle = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.regularTextColor};
`;

function toneFor(liftTooltipKey: string | undefined): Tone {
  if (liftTooltipKey === "leadingA") return "a";
  if (liftTooltipKey === "leadingB") return "b";
  return "neutral"; // "noWinner" and any unrecognized key
}

function leaderMessageFor(liftTooltipKey: string | undefined) {
  if (liftTooltipKey === "leadingA") return messages.lift.leadingA;
  if (liftTooltipKey === "leadingB") return messages.lift.leadingB;
  return messages.lift.noWinner;
}

export function LiftCell({ value, rows }: RendererProps<number | undefined>) {
  const intl = useIntl();
  const row = (rows as unknown) as LiftCellRowData | undefined;

  // Before there is a verdict there is still something worth reporting: how far along the sample
  // is. A bare "—" made every collecting experiment look identical to a broken one.
  if (!row?.hasLift) {
    const progress = row?.sampleProgress ?? 0;
    if (progress <= 0) {
      return <>—</>;
    }
    // No confidence subtitle here on purpose: backoffice-manager's `EnrichedRow.insufficientData`
    // hard-codes `confidence = 0.0` for every row that reaches this branch, so printing it would
    // report a measured "0 %" for experiments that do have a real confidence on the results page.
    return (
      <div data-testid="lift-cell-sample-progress">
        <Value $tone="neutral">
          {intl.formatMessage(messages.lift.sampleProgress, {
            pct: Math.round(progress * 100),
          })}
        </Value>
      </div>
    );
  }

  const tone = toneFor(row.liftTooltipKey);
  const leaderLabel = intl.formatMessage(leaderMessageFor(row.liftTooltipKey));
  const confidenceRounded = Math.round(row.confidence ?? 0);
  // `toFixed` always writes an English decimal point, whatever the UI language is.
  const liftFormatted = `${intl.formatNumber(value ?? 0, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
  const subtitle = intl.formatMessage(messages.lift.subtitle, {
    leader: leaderLabel,
    confidence: confidenceRounded,
  });

  return (
    <OverlayTrigger
      placement="top"
      overlay={props => (
        <Tooltip id={`lift-tooltip-${row.id}`} {...props}>
          {subtitle}
        </Tooltip>
      )}>
      <div>
        <Value $tone={tone}>{liftFormatted}</Value>
        <Subtitle>{subtitle}</Subtitle>
      </div>
    </OverlayTrigger>
  );
}
